import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const { t, i18n } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = currentLang === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
  };

  const scrollToServices = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || menuOpen ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/30 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
            <img
              src="https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/9c29b555966670d4d619fe919880326b.png"
              alt="Logo"
              className="h-9 w-9 md:h-12 md:w-12 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-sm md:text-lg font-medium text-gray-900">
                {currentLang === 'zh' ? '阳光灵性疗愈' : 'Sunshine Healing'}
              </span>
              <span className="text-xs text-gray-600">Find Your Balance in the SUN&apos;S Embrace</span>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm font-medium text-gray-900 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.about')}
            </Link>
            <a href="#services" onClick={scrollToServices} className="text-sm font-medium text-gray-900 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.services')}
            </a>
            <a href="#booking" className="text-sm font-medium text-gray-900 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.booking')}
            </a>
          </div>

          {/* Desktop Right Actions */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://wa.me/6593651286"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full text-sm font-medium transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base"></i>
              </div>
              WhatsApp
            </a>
            <button
              onClick={toggleLanguage}
              className="px-5 py-2 bg-teal-800 text-amber-400 rounded-full text-sm font-medium hover:bg-teal-900 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              {currentLang === 'zh' ? 'EN' : '中文'}
            </button>
          </div>

          {/* Mobile Right Actions */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://wa.me/6593651286"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-9 h-9 bg-[#25D366] text-white rounded-full shadow-sm"
            >
              <i className="ri-whatsapp-line text-lg"></i>
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center justify-center w-9 h-9 rounded-full bg-stone-100 text-gray-700 cursor-pointer"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-stone-100 px-4 py-4 space-y-1">
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-stone-50 text-gray-800 font-medium cursor-pointer"
          >
            <i className="ri-home-line text-teal-700"></i>
            {t('nav.about')}
          </Link>
          <a
            href="#services"
            onClick={scrollToServices}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-stone-50 text-gray-800 font-medium cursor-pointer"
          >
            <i className="ri-heart-pulse-line text-teal-700"></i>
            {t('nav.services')}
          </a>
          <a
            href="#booking"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-stone-50 text-gray-800 font-medium cursor-pointer"
          >
            <i className="ri-calendar-check-line text-teal-700"></i>
            {t('nav.booking')}
          </a>
          <div className="pt-2 border-t border-stone-100 flex gap-2">
            <button
              onClick={() => { toggleLanguage(); setMenuOpen(false); }}
              className="flex-1 py-2.5 bg-teal-800 text-amber-400 rounded-full text-sm font-medium cursor-pointer"
            >
              {currentLang === 'zh' ? 'EN' : '中文'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
